/**
 * `/flashcards` — the learner's flashcard decks (P-SCH-D).
 *
 * Reads `learning.learner.flashcards.decks.list` (own decks only, with a
 * `cardCount`) and renders a card grid. A "New deck" dialog creates one via
 * `decks.create` and navigates straight into `/flashcards/$deckId`. Each deck
 * card opens its cards; a quiet delete (two-step ConfirmDialog) removes it.
 *
 * Decks + cards are learner-OWNED (the server enforces `ownerUserId =
 * session.userId`); this screen never takes an owner input. Mobile-first:
 * single-column grid at phone width, two/three columns from `sm`/`lg`.
 */
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import { Layers, Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  Skeleton,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';

export const Route = createFileRoute('/flashcards/')({
  component: FlashcardsPage,
});

type Deck =
  inferRouterOutputs<AppRouter>['learning']['learner']['flashcards']['decks']['list'][number];

function FlashcardsPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Flashcards">
        <FlashcardsContent />
      </LearnShell>
    </RequireAuth>
  );
}

function FlashcardsContent(): React.JSX.Element {
  const decksQ = trpc.learning.learner.flashcards.decks.list.useQuery();
  const [createOpen, setCreateOpen] = React.useState(false);
  const decks = decksQ.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h2 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight">
            <span className="flex size-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Layers className="size-5" aria-hidden="true" />
            </span>
            Flashcards
          </h2>
          <p className="text-sm text-muted-foreground">
            Drill the facts until they’re automatic — make a deck and run a quick flip test.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="new-deck"
          className="press min-h-touch"
        >
          <Plus className="size-4" aria-hidden="true" />
          New deck
        </Button>
      </div>

      {decks.length > 0 ? (
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-base font-semibold tracking-tight">Your decks</h3>
          <span className="text-sm text-muted-foreground tabular-nums">
            {decks.length} {decks.length === 1 ? 'deck' : 'decks'}
          </span>
        </div>
      ) : null}

      {decksQ.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Card key={i} className="card-soft">
              <CardContent className="space-y-4 p-5">
                <div className="flex items-start gap-3">
                  <Skeleton className="size-11 shrink-0 rounded-xl" />
                  <div className="flex-1 space-y-2 pt-1">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-2/5" />
                  </div>
                </div>
                <Skeleton className="h-9 w-full rounded-md" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : decksQ.error ? (
        <EmptyState
          icon={Layers}
          title="Couldn’t load your decks"
          description={decksQ.error.message}
          action={
            <Button variant="outline" onClick={() => void decksQ.refetch()}>
              Try again
            </Button>
          }
        />
      ) : decks.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No decks yet"
          description="Create your first deck to start building flashcards."
          action={
            <Button onClick={() => setCreateOpen(true)} className="press">
              <Plus className="size-4" aria-hidden="true" />
              New deck
            </Button>
          }
        />
      ) : (
        <div className="ls-stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {decks.map((deck) => (
            <DeckCard key={deck.id} deck={deck} />
          ))}
        </div>
      )}

      <CreateDeckDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function DeckCard({ deck }: { deck: Deck }): React.JSX.Element {
  const utils = trpc.useUtils();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const remove = trpc.learning.learner.flashcards.decks.delete.useMutation({
    onSuccess: () => {
      setConfirmOpen(false);
      void utils.learning.learner.flashcards.decks.list.invalidate();
    },
  });

  const canStudy = deck.cardCount > 0;

  return (
    <Card data-testid="deck-card" className="card-soft card-lift flex flex-col">
      <CardContent className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-start gap-3">
          <span
            className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
            aria-hidden="true"
          >
            <Layers className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base leading-snug">
              <Link
                to="/flashcards/$deckId"
                params={{ deckId: deck.id }}
                className="break-words hover:underline focus-visible:underline focus-visible:outline-none"
                data-testid="open-deck"
              >
                {deck.title}
              </Link>
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground tabular-nums">
              {deck.cardCount} {deck.cardCount === 1 ? 'card' : 'cards'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="-mr-2 -mt-1 shrink-0 text-muted-foreground hover:text-destructive"
            aria-label={`Delete deck ${deck.title}`}
            data-testid="delete-deck"
            onClick={() => setConfirmOpen(true)}
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </Button>
        </div>

        {deck.description ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">{deck.description}</p>
        ) : null}

        <div className="mt-auto">
          <Button asChild variant="outline" size="sm" className="press min-h-touch w-full">
            <Link to="/flashcards/$deckId" params={{ deckId: deck.id }}>
              {canStudy ? 'Open & study' : 'Open deck'}
            </Link>
          </Button>
        </div>
      </CardContent>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!remove.isPending) setConfirmOpen(open);
        }}
        title="Delete this deck?"
        description={`"${deck.title}" and all its cards will be permanently removed.`}
        confirmLabel="Delete deck"
        cancelLabel="Keep deck"
        destructive
        pending={remove.isPending}
        error={remove.error?.message ?? null}
        onConfirm={() => remove.mutate({ id: deck.id })}
      />
    </Card>
  );
}

function CreateDeckDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');

  const create = trpc.learning.learner.flashcards.decks.create.useMutation({
    onSuccess: (res) => {
      void utils.learning.learner.flashcards.decks.list.invalidate();
      onOpenChange(false);
      setTitle('');
      setDescription('');
      void navigate({ to: '/flashcards/$deckId', params: { deckId: res.id } });
    },
  });

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (title.trim() === '' || create.isPending) return;
    create.mutate({
      title: title.trim(),
      ...(description.trim() !== '' ? { description: description.trim() } : {}),
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!create.isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="animate-scale-in">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>New deck</DialogTitle>
            <DialogDescription>Give your deck a name — you can add cards next.</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="deck-title">Title</Label>
            <Input
              id="deck-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. SAT vocabulary"
              data-testid="deck-title"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deck-description">Description (optional)</Label>
            <Input
              id="deck-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this deck is for"
              data-testid="deck-description"
            />
          </div>

          {create.error ? (
            <p className="text-sm text-destructive" role="alert">
              {create.error.message}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={title.trim() === '' || create.isPending}
              data-testid="deck-create-submit"
              className="press"
            >
              {create.isPending ? 'Creating…' : 'Create deck'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
